import { FC, useState } from 'react';
import { Card, CardBody, Icon } from '@aidkitorg/component-library';
import { useHistory } from 'react-router-dom';
import { useLocalizedStrings } from '../../../../Localization';
import { RelatedCasesSectionProps } from '../shared/types';

/**
 * Related cases section with expandable card showing related support cases
 */
export const RelatedCasesSection: FC<RelatedCasesSectionProps> = ({ relatedCases }) => {
  const L = useLocalizedStrings();
  const history = useHistory();
  const [relatedCasesExpanded, setRelatedCasesExpanded] = useState(false);

  if (relatedCases.length === 0) {
    return null;
  }

  return (
    <div className="min-w-0">
      <Card className="bg-yellow-50 rounded-lg border-2 !border-yellow-400 min-w-0">
        <CardBody>
          <button
            onClick={() => setRelatedCasesExpanded(!relatedCasesExpanded)}
            className="w-full flex items-center justify-between hover:opacity-80 transition-opacity"
          >
            <div className="flex items-center gap-2">
              <Icon
                role="decorative"
                type="solid"
                name="ExclamationTriangleIcon"
                className="text-yellow-600 w-5 h-5"
              />
              <span className="text-sm font-semibold text-gray-900">
                {relatedCases.length} {relatedCases.length === 1 ? 'Related Case Found' : (L.support.related_support_cases_found || 'Related Cases Found')}
              </span>
            </div>
            <Icon
              role="decorative"
              type="solid"
              name="ChevronRightIcon"
              className={`w-5 h-5 text-gray-600 transition-transform ${relatedCasesExpanded ? 'rotate-90' : ''}`}
            />
          </button>

          {relatedCasesExpanded && (
            <div className="space-y-2 mt-3">
              {relatedCases.slice(0, 3).map((relatedCase) => {
                if (!relatedCase.row.support_case) return null;

                // Get status badge colors
                const statusColors = {
                  'open': 'bg-yellow-100 text-yellow-800',
                  'resolved': 'bg-green-100 text-green-800',
                  'escalated': 'bg-red-100 text-red-800',
                  'viewed': 'bg-blue-100 text-blue-800',
                }[relatedCase.row.support_case_status || 'open'] || 'bg-gray-100 text-gray-800';

                // Get most recent message
                const lastMessage = relatedCase.row.last_message_text || 'No messages';

                return (
                  <div
                    key={relatedCase.row.support_case}
                    onClick={() => history.push(`/admin-panel/support/${relatedCase.row.support_case}`)}
                    className="block p-3 bg-white rounded-lg border border-yellow-200 hover:border-yellow-400 hover:shadow-sm transition-all cursor-pointer"
                    style={{ textDecoration: 'none' }}
                  >
                    <div className="space-y-2">
                      {/* Header row with case ID and status */}
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-xs font-semibold text-gray-900 break-words">
                          Case {relatedCase.row.support_case.split('-')[0]}
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${statusColors}`}>
                          {relatedCase.row.support_case_status || 'Open'}
                        </span>
                      </div>

                      {/* Assigned to */}
                      <div className="text-xs text-gray-600 break-words">
                        <span className="font-medium">Assigned:</span> {relatedCase.row.assigned_to_name || 'Unassigned'}
                      </div>

                      {/* Most recent message */}
                      <div className="text-xs text-gray-500 line-clamp-2 break-words">
                        {lastMessage}
                      </div>
                    </div>
                  </div>
                );
              })}
              {relatedCases.length > 3 && (
                <div className="text-xs text-gray-600 text-center pt-1">
                  +{relatedCases.length - 3} more
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
